import React, { useMemo, useRef } from 'react'
import { FileDrop } from 'react-file-drop'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from './shadcn/Button'
import { useCore } from '../hooks'
import { faUpload, faFileWord } from '@fortawesome/pro-solid-svg-icons'
import { downloadFileWithAuth, openFileInNewTabWithAuth, printDocument } from '../lib'
import { useGQLMutation } from '../hooks/GQLUtilityHooks'
import { FragmentType, graphql } from '../gql'
import { faPrint, faEye, faCloudUpload, faSpinner } from '@fortawesome/pro-solid-svg-icons'
import { useQueryClient } from '@tanstack/react-query'
import { toastSuccess } from '../lib/toastHelpers'
import { useOrderDetailContext } from '../views/OrderDetails/OrderDetailContext'
import { useFragment } from '../gql'
import { FragmentValue } from '../lib/RequestUtils'
import { Tag } from './Tag'
import { FileCard } from './FileCard'

const CreateFileMutation = graphql(`
  mutation CreateFile(
    $base64Data: String!
    $text: String!
    $originalFilename: String!
    $mimeType: String!
    $order: Int
  ) {
    createFile(
      base64Data: $base64Data
      text: $text
      originalFilename: $originalFilename
      mimeType: $mimeType
      order: $order
    ) {
      data {
        id
        text
        originalFilename
        mimeType
        size
        url
      }
      errors
    }
  }
`)
const DeleteFileMutation = graphql(`
  mutation DeleteFile($id: Int!) {
    deleteFile(id: $id) {
      errors
    }
  }
`)

const OrderFiles_OrderFragment = graphql(`
  fragment OrderFiles_OrderFragment on Order {
    id
    files {
      id
      url
      originalFilename
      mimeType
      size
    }
    reservationUrl
    invoiceUrl
    movePacks {
      id
      pdfWOPricesUrl
    }
    items {
      product {
        id
        name
        mainImage {
          thumbnails {
            format
            url
          }
        }
        tags {
          id
          ...TagComponent_TagFragment
        }
        files {
          id
          url
          originalFilename
          mimeType
          size
        }
      }
    }
  }
`)

type FileType = Omit<FragmentValue<typeof OrderFiles_OrderFragment>['files'][number], 'id'> & {
  id: string | number
  isPdf: boolean
  product?: FragmentValue<typeof OrderFiles_OrderFragment>['items'][number]['product']
}

export const OrderFiles: React.FC = () => {
  const context = useOrderDetailContext()
  const order = useFragment(OrderFiles_OrderFragment, context.order)
  const { core, isFetching, setIsFetching } = useCore()
  const { mutateAsync: createFileMutation, isPending: isCreating } = useGQLMutation(CreateFileMutation, ['createFile'])
  const queryClient = useQueryClient()
  const {
    mutateAsync: deleteFileMutation,
    isPending: isDeleting,
    variables: deletingVariables,
  } = useGQLMutation(DeleteFileMutation, ['deleteFile'], {
    onMutate: async variables => {
      queryClient.setQueryData(['order', { id: order.id }], (old: any) => {
        return {
          order: {
            ...old.order,
            data: {
              ...old.order.data,
              files: old.order.data.files.filter(file => file.id !== variables.id),
            },
          },
        }
      })
    },
  })
  const inputRef = useRef<HTMLInputElement>(null)

  const fileToBase64 = (file: File): Promise<string | any> => {
    return new Promise((resolve, reject) => {
      if (!!file) {
        const reader = new FileReader()
        reader.readAsBinaryString(new Blob([file]))
        reader.onload = evt => {
          resolve({
            text: file.name,
            originalFilename: file.name,
            mimeType: file.type,
            base64Data: btoa(String(evt?.target?.result)),
          })
        }
        reader.onerror = () => {
          reject('Došlo k chybě při zpracování souboru')
        }
      } else {
        reject('No file')
      }
    })
  }

  const uploadFiles = async (droppedFiles: FileList) => {
    const files: File[] = Array.from(droppedFiles)
    const promisesBase64s = files.map(f => fileToBase64(f))
    setIsFetching(true)
    try {
      const resolvedBase64s = await Promise.all(promisesBase64s)
      await Promise.all(
        resolvedBase64s.map(b =>
          createFileMutation({
            base64Data: b.base64Data,
            text: b.text,
            originalFilename: b.originalFilename,
            mimeType: b.mimeType,
            order: order.id,
          }),
        ),
      )
      toastSuccess('Soubory nahrány')
      queryClient.invalidateQueries({ queryKey: ['order', { id: order.id }] })
    } catch (e: any) {
      console.warn(`uploadFiles - error: ${e.message || e.stack}`)
    } finally {
      setIsFetching(false)
    }
  }

  const deleteFile = async (file: FileType) => {
    if (confirm('Opravdu smazat?')) {
      try {
        await deleteFileMutation({ id: Number(file.id) })
        toastSuccess('Soubory smazány')
        queryClient.invalidateQueries({ queryKey: ['order', { id: order.id }] })
      } catch (e: any) {
        console.warn(`deleteFile - error: ${e.message || e.stack}`)
      }
    }
  }

  const deleteAll = async () => {
    if (confirm('Opravdu smazat?')) {
      try {
        await Promise.all(order.files.map(file => deleteFileMutation({ id: Number(file.id) })))
        toastSuccess('Soubory smazány')
        queryClient.invalidateQueries({ queryKey: ['order', { id: order.id }] })
      } catch (e: any) {
        console.warn(`deleteAll - error: ${e.message || e.stack}`)
      }
    }
  }

  const showFile = (file: FileType) => {
    window.open(file.url, '_blank')
  }

  const viewFile = (file: FileType) => {
    if (file.isPdf) {
      openFileInNewTabWithAuth(file.url, core)
    } else {
      showFile(file)
    }
  }

  const allFiles = useMemo(() => {
    let files: FileType[] = []

    if (order.reservationUrl) {
      files.push({ id: 'reservationUrl', url: order.reservationUrl, originalFilename: 'Objednávka', isPdf: true })
    }

    if (order.invoiceUrl) {
      files.push({ id: 'invoiceUrl', url: order.invoiceUrl, originalFilename: 'Faktura', isPdf: true })
    }

    if (order.movePacks?.length && (order.movePacks[0] as any).pdfWOPricesUrl) {
      files.push({
        id: 'movePack',
        url: (order.movePacks[0] as any).pdfWOPricesUrl,
        originalFilename: 'Dodací list',
        isPdf: true,
      })
    }

    order.files.forEach(file => {
      files.push({
        id: file.id,
        originalFilename: file.originalFilename || `Soubor ${file.id}`,
        url: file.url,
        size: file.size,
        isPdf: file.mimeType === 'application/pdf',
      })
    })

    return files
  }, [order])

  return (
    <div className="p-main space-y-6 w-[1200px] mx-auto">
      <div className="flex justify-between items-center w-full">
        <h3 className="text-3xl font-bold text-gray-900">Soubory</h3>
      </div>

      {/* Files Grid */}
      {allFiles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allFiles.map(file => (
            <FileCard
              key={file.id}
              id={file.id}
              originalFilename={file.originalFilename}
              url={file.url}
              isPdf={file.isPdf}
              size={file.size}
              productName={file.product?.name}
              onView={() => viewFile(file)}
              onPrint={file.isPdf ? () => printDocument(file.url, false, false, core) : undefined}
              onDownload={
                !file.isPdf || !window._depoto
                  ? () => downloadFileWithAuth(file.url, file.originalFilename, core)
                  : undefined
              }
              onDelete={!file.isPdf && !file.product && file.id !== 'movePack' ? () => deleteFile(file) : undefined}
              showPrint={file.isPdf}
              showDownload={!file.isPdf || !window._depoto}
              showDelete={!file.isPdf && !file.product && file.id !== 'movePack'}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <FontAwesomeIcon icon={faFileWord} className="text-4xl mb-2 opacity-50" />
          <p>Zatím nejsou nahrány žádné soubory</p>
        </div>
      )}

      {/* Product Files Section */}
      {order.items
        .filter(item => item.product?.files?.length)
        .map(item => {
          const product = item.product
          if (!product) return null

          return (
            <div key={product.id} className="space-y-4">
              {/* Product Header */}
              <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                <div className="flex-1 flex items-center gap-3">
                  <img
                    src={product.mainImage?.thumbnails?.find(t => t.format === 'w135')?.url}
                    alt={product.name}
                    className="w-16 h-16 rounded-xl"
                  />
                  <div className="flex flex-1 flex-col">
                    <h4 className="text-2xl font-semibold text-gray-900">{product.name}</h4>
                    <p className="text-sm text-gray-500">
                      {product.files?.length} {product.files?.length === 1 ? 'soubor' : 'souborů'}
                    </p>
                  </div>
                </div>
                {(product.tags || []).map(tag => <Tag key={tag.id} tag={tag} />) || null}
              </div>

              {/* Product Files Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {product.files?.map(file => {
                  const isPdf = file.mimeType === 'application/pdf'
                  return (
                    <FileCard
                      key={file.id}
                      id={file.id}
                      originalFilename={file.originalFilename}
                      url={file.url}
                      isPdf={isPdf}
                      size={file.size}
                      onView={() => viewFile(file as FileType)}
                      onDownload={
                        !isPdf ? () => downloadFileWithAuth(file.url, file.originalFilename, core) : undefined
                      }
                      onPrint={isPdf ? () => printDocument(file.url, false, false, core) : undefined}
                      showPrint={isPdf}
                      showDownload={!file.isPdf || !window._depoto}
                    />
                  )
                })}
              </div>
            </div>
          )
        })}

      {/* File Upload Section */}
      <div className="border-t pt-6">
        <input type="file" ref={inputRef} hidden multiple onChange={e => uploadFiles(e.target.files)} />

        <div className="flex gap-3 mb-4"></div>

        <FileDrop onDrop={(files, event) => uploadFiles(files)} onTargetClick={() => inputRef.current?.click()}>
          {isCreating && (
            <div className="bg-white bg-opacity-60 flex items-center justify-center rounded-2xl p-8 h-80">
              <FontAwesomeIcon icon={faSpinner} className="text-4xl text-gray-400 mb-4 animate-spin" />
            </div>
          )}
          {!isCreating && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer gap-4 flex flex-col items-center">
              <FontAwesomeIcon icon={faCloudUpload} className="text-4xl text-gray-400 mb-4" />
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-700">Přetáhněte soubory sem</p>
                <p className="text-sm text-gray-500">nebo klikněte na tlačítko "Vybrat soubory" výše</p>
                <p className="text-xs text-gray-400">Podporované formáty: PDF, DOC, DOCX, JPG, PNG a další</p>
              </div>
              <Button variant="secondary" icon={faUpload} disabled={isFetching}>
                Vybrat soubory
              </Button>
            </div>
          )}
        </FileDrop>
      </div>
    </div>
  )
}
